import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Button } from "@app-components";
import AuthProvider from "@/providers/AuthProvider";
import { auth } from "@/lib/firebase";
import { env, ENVIRONMENT } from "@/lib";

type CompletionStatus =
  | "completing"
  | "done"
  | "no-access"
  | "expired"
  | "error";

const VITE_API_BASE = env("VITE_API_BASE");
const REDIRECT_DELAY = 20;
const STATUS_COLOR_MAP: Record<CompletionStatus, string> = {
  completing: "var(--wlcm-color-blue)",
  done: "var(--wlcm-color-success-green)",
  "no-access": "var(--wlcm-color-danger-red)",
  expired: "var(--wlcm-color-warning-orange)",
  error: "var(--wlcm-color-danger-red)",
};

function FoodLoginPage() {
  const { session: sessionId } = Route.useSearch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<CompletionStatus>("completing");
  const [countdown, setCountdown] = useState(REDIRECT_DELAY);

  useEffect(() => {
    const complete = async () => {
      const user = auth.currentUser;
      if (!user) {
        setStatus("error");
        return;
      }
      try {
        const idToken = await user.getIdToken();
        const res = await fetch(
          `${VITE_API_BASE}/api/food/completeQrLoginSession`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ sessionId }),
          },
        );
        if (res.ok) {
          setStatus("done");
        } else if (res.status === 403) {
          setStatus("no-access");
        } else if (res.status === 410) {
          setStatus("expired");
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    };
    complete();
  }, [sessionId]);

  useEffect(() => {
    if (status === "completing") return;
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          if (ENVIRONMENT !== "dev") navigate({ to: "/" });
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [status, navigate]);

  const titles: Record<CompletionStatus, string> = {
    completing: t("FOOD_LOGIN.COMPLETING_TITLE"),
    done: t("FOOD_LOGIN.DONE_TITLE"),
    "no-access": t("FOOD_LOGIN.NO_ACCESS_TITLE"),
    expired: t("FOOD_LOGIN.EXPIRED_TITLE"),
    error: t("FOOD_LOGIN.ERROR_TITLE"),
  };

  const bodies: Record<CompletionStatus, string> = {
    completing: t("FOOD_LOGIN.COMPLETING_BODY"),
    done: "",
    "no-access": t("FOOD_LOGIN.NO_ACCESS_BODY"),
    expired: t("FOOD_LOGIN.EXPIRED_BODY"),
    error: t("FOOD_LOGIN.ERROR_BODY"),
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100dvh",
        gap: "var(--wlcm-spacing-md)",
        padding: "var(--wlcm-spacing-xl)",
        textAlign: "center",
        color: "white",
      }}
    >
      <h1
        style={{
          fontSize: "var(--wlcm-text-lg)",
          margin: 0,
          color: STATUS_COLOR_MAP[status],
        }}
      >
        {titles[status]}
      </h1>
      <p style={{ fontSize: "var(--wlcm-text-md)", margin: 0 }}>
        {bodies[status]}
      </p>

      {status !== "completing" && (
        <>
          <p style={{ margin: 0, opacity: 0.5 }}>
            {t("FOOD_LOGIN.CLOSE_TAB")}{" "}
            {t("FOOD_LOGIN.REDIRECT_COUNTDOWN", { seconds: countdown })}
          </p>
          <Button onClick={() => navigate({ to: "/" })}>
            {t("FOOD_LOGIN.GO_TO_DASHBOARD")}
          </Button>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute("/food-login")({
  validateSearch: z.object({ session: z.string() }),
  component: () => (
    <AuthProvider>
      <FoodLoginPage />
    </AuthProvider>
  ),
});
